/* ============================================================
   ÉKO '77 — HOME HERO

   Port of the two hero loops in the reference inline script
   (https://eko77-redesign.pages.dev/):

   1. THE PLATE GROWS WHILE THE SECTION IS PINNED.
      Reference maths, kept exactly:
        sh     = sticky.offsetHeight            // the stage, not the window
        travel = max(1, hero.offsetHeight - sh)
        p      = clamp(scrolled / travel, 0, 1)
        e      = p * p * (3 - 2 * p)            // smoothstep, so it settles
        narrow = innerWidth < 700
        w0     = narrow ? vw * .86 : min(vw * .62, 1060)
        h0     = sh * (narrow ? .60 : .56)
        --plate-w = lerp(w0, vw, e)   --plate-h = lerp(h0, sh, e)
        --quote-o = clamp(1 - p / .42, 0, 1)
      Every constant above is a schema setting here, defaulting to the
      reference number.

      ONE DELIBERATE CHANGE: the reference reads `window.scrollY`
      straight, which silently assumes the hero starts at document y 0.
      This reads the hero's own offset first, so the curve is identical
      on the page as built and stays correct if anything is ever placed
      above the hero (an announcement bar, a second section).

      `window.innerWidth` is kept for vw — NOT clientWidth. Both look
      identical (the stage clips), but innerWidth is what the reference
      measures and the plate widths have to match it number for number.

   2. THE FOOTAGE IS LAZY, AND OPTIONAL.
      The <video> ships with `preload="none"` and the file in data-src,
      so nothing is fetched until the script decides it is worth it:
      never under reduced motion, never under Save-Data, and never
      below the width setting (900px in the reference — paid traffic
      lands on phones and a 2.5 MB autoplay hero is not worth the first
      paint). Below that the poster IS the hero. When it does load, an
      IntersectionObserver plays it only while it is on screen.

   Both loops are re-entrant: the theme editor re-renders a section by
   replacing its DOM, which disconnects this element and connects a new
   one, so listeners and observers are torn down in disconnectedCallback.
   ============================================================ */

(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  class XHero extends HTMLElement {
    connectedCallback() {
      this.hero = this.querySelector('[data-x-hero]');
      this.stage = this.querySelector('[data-x-hero-stage]');
      this.plate = this.querySelector('[data-x-hero-plate]');
      this.video = this.querySelector('[data-x-hero-video]');

      if (!this.hero || !this.stage || !this.plate) return;

      this.narrowBelow = num(this.dataset.narrowBelow, 700);
      this.plateW = num(this.dataset.plateWidth, 62) / 100;
      this.plateMax = num(this.dataset.plateMax, 1060);
      this.plateH = num(this.dataset.plateHeight, 56) / 100;
      this.plateWm = num(this.dataset.plateWidthMobile, 86) / 100;
      this.plateHm = num(this.dataset.plateHeightMobile, 60) / 100;
      this.quoteFade = num(this.dataset.quoteFade, 42) / 100 || 0.42;
      this.videoMin = num(this.dataset.videoMinWidth, 900);

      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.motion = window.matchMedia(REDUCED);
      if (typeof this.motion.addEventListener === 'function') {
        this.motion.addEventListener('change', this.onMotionChange);
      } else if (typeof this.motion.addListener === 'function') {
        this.motion.addListener(this.onMotionChange);
      }

      this.start();
      this.bindVideo();
    }

    disconnectedCallback() {
      this.stopScroll();
      if (this.motion) {
        if (typeof this.motion.removeEventListener === 'function') {
          this.motion.removeEventListener('change', this.onMotionChange);
        } else if (typeof this.motion.removeListener === 'function') {
          this.motion.removeListener(this.onMotionChange);
        }
      }
      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }
    }

    /* ---------------- 1. the plate ---------------- */

    start() {
      if (this.motion.matches) return this.flatten();

      this.hero.classList.remove('x-hero--flat');
      this.frame();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.scrolling = true;
    }

    stopScroll() {
      if (!this.scrolling) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.scrolling = false;
    }

    /* The end state, held still. The class carries it (see x-hero.css)
       rather than inline styles, so leaving reduced motion is a class
       removal and not a pile of properties to unset one by one. */
    flatten() {
      this.stopScroll();
      this.hero.classList.add('x-hero--flat');
      this.plate.style.removeProperty('--plate-w');
      this.plate.style.removeProperty('--plate-h');
      this.plate.style.setProperty('--quote-o', '1');
    }

    onMotionChange() {
      if (this.motion.matches) {
        this.flatten();
        if (this.video) this.video.pause();
      } else {
        this.start();
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(
        function () {
          this.frame();
        }.bind(this)
      );
    }

    frame() {
      this.ticking = false;

      var vw = window.innerWidth;
      var sh = this.stage.offsetHeight;
      var travel = Math.max(1, this.hero.offsetHeight - sh);
      var top = this.hero.getBoundingClientRect().top + window.scrollY;
      var p = clamp((window.scrollY - top) / travel, 0, 1);
      var e = p * p * (3 - 2 * p);

      var narrow = vw < this.narrowBelow;
      var w0 = narrow ? vw * this.plateWm : Math.min(vw * this.plateW, this.plateMax);
      var h0 = sh * (narrow ? this.plateHm : this.plateH);

      this.plate.style.setProperty('--plate-w', lerp(w0, vw, e) + 'px');
      this.plate.style.setProperty('--plate-h', lerp(h0, sh, e) + 'px');
      this.plate.style.setProperty('--quote-o', clamp(1 - p / this.quoteFade, 0, 1));
    }

    /* ---------------- 2. the footage ---------------- */

    bindVideo() {
      var video = this.video;
      if (!video) return;

      var src = video.getAttribute('data-src');
      var saveData = !!(navigator.connection && navigator.connection.saveData);
      var wanted =
        !!src && !this.motion.matches && window.innerWidth >= this.videoMin && !saveData;

      if (!wanted) {
        /* the poster is the hero: nothing is fetched, and the element
           is not left looping an empty source */
        video.removeAttribute('loop');
        return;
      }

      video.addEventListener(
        'canplay',
        function () {
          video.classList.add('is-ready');
        },
        { once: true }
      );

      var source = document.createElement('source');
      source.src = src;
      source.type = video.getAttribute('data-type') || 'video/mp4';
      video.appendChild(source);
      video.load();

      if (!('IntersectionObserver' in window)) {
        video.play().catch(function () {});
        return;
      }

      this.videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) video.play().catch(function () {});
            else video.pause();
          });
        },
        { threshold: 0.05 }
      );
      this.videoObserver.observe(video);
    }
  }

  if (!customElements.get('x-hero')) customElements.define('x-hero', XHero);
})();
